import React from 'react';
import propTypes from 'prop-types';
import {
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionIcon,
  AccordionPanel,
  Box,
  Divider,
  Link,
  SimpleGrid,
  Stack,
} from '@chakra-ui/react';
import { Button } from '@outdoorly/button';
import { Select } from '@outdoorly/select';
import { Text } from '@outdoorly/text';
import { Field, TextInput, TextareaInput } from '@outdoorly/input';
import { useForm, Controller } from 'react-hook-form';
import parsePhoneNumber from 'libphonenumber-js';
import NextImage from 'next/image';

import { EMAIL_REGEX } from '../globals';

const isValidUSPhoneNumber = (value) => {
  try {
    const phoneNumber = parsePhoneNumber(value, 'US');
    return phoneNumber?.isValid() && phoneNumber.country === 'US';
  } catch (error) {
    return false;
  }
};

const ContactForm = ({ user = {}, orderNumber }) => {
  const defaultValues = { name: user.name, email: user.email, orderNumber };
  const { register, handleSubmit, control, errors, setValue } = useForm({
    defaultValues,
  });
  const onSubmit = (data) => console.log(data);

  const subjectOptions = [
    {
      value: 'order-problem',
      label: 'There’s a problem with the order I received',
    },
    {
      value: 'other',
      label: 'Other',
    },
  ];

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={5}>
        <SimpleGrid columns={2} spacing={5} minChildWidth="280px">
          <Field label="What's going on?" fontSize="xl">
            <Controller
              name="subject"
              control={control}
              defaultValue={subjectOptions[0]}
              rules={{ required: true }}
              render={({ value, onChange }) => {
                return (
                  <Select
                    options={subjectOptions}
                    value={value}
                    onChange={onChange}
                  />
                );
              }}
            />
          </Field>
          <Field label="Order Number" error={errors.orderNumber?.message}>
            <TextInput
              name="orderNumber"
              ref={register({
                required: { value: true, message: 'Order number is required' },
              })}
            />
          </Field>
          <Field label="What's your name?" error={errors.name?.message}>
            <TextInput
              name="name"
              ref={register({
                required: { value: true, message: 'Name is required' },
              })}
            />
          </Field>
          <Field label="Email Address" error={errors.email?.message}>
            <TextInput
              type="email"
              name="email"
              ref={register({
                required: { value: true, message: 'Email is required' },
                pattern: {
                  value: EMAIL_REGEX,
                  message: 'Invalid email address',
                },
              })}
              onChange={(input) => {
                setValue('email', input?.target?.value?.trim());
              }}
            />
          </Field>
        </SimpleGrid>
        <Field label="Your Message" error={errors.message?.message}>
          <TextareaInput
            name="message"
            ref={register({
              required: { value: 'true', message: 'Message is required' },
            })}
          />
        </Field>
        <Field label="Attach Image">
          <Box
            minH={155}
            borderWidth={1}
            borderStyle="dashed"
            borderColor="scheme.controlActive"
            borderRadius={8}
          />
        </Field>
        <Box textAlign={['center', 'center', 'left']} w="100%">
          <Button
            w={260}
            colorScheme="red"
            variant="solid"
            mx={['auto', 'auto', 0]}
            onClick={handleSubmit(onSubmit)}
          >
            Send Message
          </Button>
        </Box>
      </Stack>
    </form>
  );
};

ContactForm.propTypes = {
  user: propTypes.shape({
    name: propTypes.string,
    email: propTypes.string,
  }),
  orderNumber: propTypes.string,
};

const FAQS = () => {
  const [phoneNumber, setPhoneNumber] = React.useState('');
  const [phoneNumberError, setPhoneNumberError] = React.useState('');
  const [hasSubmittedMessagingOptIn, setHasSubmittedMessagingOptIn] =
    React.useState(false);

  const handleMessagingOptInSubmit = (event) => {
    event.preventDefault();

    if (!isValidUSPhoneNumber(phoneNumber)) {
      setPhoneNumberError('Please enter a valid US phone number');
      setHasSubmittedMessagingOptIn(false);
      return;
    }

    setPhoneNumberError('');
    setHasSubmittedMessagingOptIn(true);
  };

  return (
    <Box borderWidth={1} borderRadius={9} borderColor="scheme.border">
      <Accordion defaultIndex={[0, 1]} allowMultiple>
        <AccordionItem borderTopWidth={0}>
          <AccordionButton p={5}>
            <Text flex="1" textAlign="left" fontWeight="medium">
              Where does Outdoorly ship?
            </Text>
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel p={5}>
            <Text>
              Currently, all of our brand partners ship to the US. Some brand
              partners are able ship to Canada.
            </Text>
          </AccordionPanel>
        </AccordionItem>
        <AccordionItem>
          <AccordionButton p={5}>
            <Text flex="1" textAlign="left" fontWeight="medium">
              What is your return policy?
            </Text>
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel p={5}>
            <Text>All sales are final. No returns and no exchanges.</Text>
          </AccordionPanel>
        </AccordionItem>
        <AccordionItem>
          <AccordionButton p={5}>
            <Text flex="1" textAlign="left" fontWeight="medium">
              Where’s my order?
            </Text>
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel p={5}>
            <Text>
              Good question. We&apos;ll send you an email with tracking info
              when your order is shipped. If the tracks are wandering off the
              trail or something seems amiss, shoot us an email at{' '}
              <Link href="mailto:help@outdoorly.com">help@outdoorly.com</Link>{' '}
              so we can try picking up the scent.
            </Text>
          </AccordionPanel>
        </AccordionItem>
        <AccordionItem>
          <AccordionButton p={5}>
            <Text flex="1" textAlign="left" fontWeight="medium">
              Want to reach us by text?
            </Text>
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel p={5}>
            <Text>
            You can text us at{' '}
            <Link href="tel:+16178418471">(617) 841-8471</Link> for support.<br />
            By texting this number, you agree to receive recurring SMS messages from Outdoorly related to your support inquiry.<br />
            Message frequency may vary. Msg &amp; data rates may apply. 
            Reply STOP to unsubscribe or HELP for help.
       
            </Text>
          </AccordionPanel>
        </AccordionItem>
        <AccordionItem _last={{ borderBottomWidth: 0 }}>
          <AccordionButton p={5}>
            <Text flex="1" textAlign="left" fontWeight="medium">
              Opt in to messaging
            </Text>
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel p={5}>
            <Stack
              as="form"
              spacing={4}
              onSubmit={handleMessagingOptInSubmit}
            >
              <Text>
                Enter your phone number below, and we&apos;ll reach out. By
                opting in, you agree to receive support-related SMS messages
                from Outdoorly. Message frequency may vary. Msg &amp; data rates
                may apply. Reply STOP to unsubscribe or HELP for help.
              </Text>
              <Field label="Phone number" error={phoneNumberError}>
                <TextInput
                  type="tel"
                  name="messagingPhoneNumber"
                  value={phoneNumber}
                  onChange={(event) => {
                    setPhoneNumber(event.target.value);
                    setPhoneNumberError('');
                    setHasSubmittedMessagingOptIn(false);
                  }}
                />
              </Field>
              <Button type="submit" colorScheme="red" variant="solid">
                Submit
              </Button>
              {hasSubmittedMessagingOptIn && (
                <Text color="scheme.textSecondary">
                  Thanks! We&apos;ll reach out by text soon.
                </Text>
              )}
            </Stack>
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
    </Box>
  );
};

const ContactEmails = () => {
  return (
    <Box borderWidth={1} borderRadius={9} borderColor="scheme.border" p={6}>
      <Stack spacing={6}>
        <Stack spacing={4}>
          <Text size="xl" fontWeight="medium">
            Support
          </Text>
          <Link href="mailto:help@outdoorly.com">help@outdoorly.com</Link>
        </Stack>
        <Divider />
        <Stack spacing={4}>
          <Text size="xl" fontWeight="medium">
            Media & Press Inquiries
          </Text>
          <Link href="mailto:press@outdoorly.com">press@outdoorly.com</Link>
        </Stack>
      </Stack>
    </Box>
  );
};

const ImageHeader = () => {
  return (
    <Box
      borderRadius={16}
      overflow="hidden"
      mt={[0, 0, 4]}
      mb={[4, 4, 12]}
      display="flex"
      direction="column"
      justifyContent="center"
      position="relative"
      maxW="100%"
      height={['2xs', null, null, 'xs']}
    >
      <NextImage
        layout="fill"
        objectFit="cover"
        src="/images/landing/rock-climb.jpg"
        alt="contact us"
        objectPosition="center"
      />
      <Box
        w="100%"
        h="100%"
        display="flex"
        justifyContent="center"
        alignItems="center"
        position="absolute"
      >
        <Text size="4xl" fontWeight="bold" color="scheme.textPrimaryInverse">
          Contact us
        </Text>
      </Box>
    </Box>
  );
};

export const Contact = () => (
  <Box
    w="100%"
    display="flex"
    direction="column"
    justifyContent="center"
    pt={4}
    pb={[8, 8, 32]}
  >
    <Stack spacing={5} w="100%" maxW={['100%', '100%', '100%', '7xl']} px={5}>
      <ImageHeader />
      <Stack
        spacing={[5, 5, 8]}
        direction={['column', 'column', 'column', 'row']}
        alignItems="start"
      >
        <Stack spacing={10}>
          <Text size="4xl" fontWeight="medium">
            How can we help you today?
          </Text>
          <Text color="scheme.textSecondary" size="lg">
            Trouble with your order? Want to chat gear? Have a brand we should
            know about? Send us an email, and we&apos;ll get back to you as soon
            as we can.
          </Text>

          <Text color="scheme.textSecondary" size="xl">
            Reach us at{' '}
            <Link href="mailto:help@outdoorly.com">help@outdoorly.com</Link>.
          </Text>

          <Button
            size="lg"
            as="a"
            href="mailto:help@outdoorly.com"
            variant="subtle"
            colorScheme="red"
          >
            Email us
          </Button>
          {/* <ContactForm user={user} orderNumber={orderNumber} /> */}
        </Stack>
        <Stack
          spacing={5}
          maxW={['100%', '100%', '100%', 'md']}
          minW={['100%', '100%', '100%', 'md']}
        >
          <FAQS />
          <ContactEmails />
        </Stack>
      </Stack>
    </Stack>
  </Box>
);

Contact.propTypes = {
  user: propTypes.shape({
    name: propTypes.string,
    email: propTypes.string,
  }),
  orderNumber: propTypes.string,
};
